/**
 * NonRefundableConfirmModal — the red-warning policy gate that fires
 * before ANY payment action (PayPal subscribe, crypto intent, token pack).
 *
 * Per the user's explicit policy:
 *   • Red AlertTriangle icon, red header, prominent visual weight
 *   • Required checkbox: "I understand and agree"
 *   • Confirm CTA disabled until the box is ticked
 *   • Clicking Agree fires the onConfirm callback so the caller can
 *     kick off the actual payment flow
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <NonRefundableConfirmModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     onConfirm={() => actuallyDoTheThing()}
 *     itemLabel="Premium plan · $19.99/mo"
 *   />
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, ShieldAlert } from "lucide-react";

export const NonRefundableConfirmModal = ({
  open,
  onOpenChange,
  onConfirm,
  itemLabel = "your purchase",
}) => {
  const [agreed, setAgreed] = useState(false);

  // Reset the agreement checkbox each time the modal opens so users have
  // to deliberately re-accept on every payment attempt.
  useEffect(() => {
    if (open) setAgreed(false);
  }, [open]);

  const confirm = () => {
    if (!agreed) return;
    onOpenChange(false);
    // Defer so the close animation can run before the next modal/redirect
    setTimeout(() => onConfirm?.(), 50);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-rose-300 max-w-md"
        data-testid="non-refundable-modal"
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-rose-50 border-2 border-rose-300 shrink-0">
              <AlertTriangle className="w-6 h-6 text-rose-600" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600">
                Final warning
              </p>
              <DialogTitle className="text-xl font-display font-black text-rose-700 tracking-tight mt-1">
                Non-refundable payment
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Confirm you understand that payments cannot be refunded before proceeding.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-2xl bg-rose-50 border-2 border-rose-200 p-4 text-sm text-rose-800 leading-relaxed"
          data-testid="non-refundable-body"
        >
          <p className="font-bold mb-2">
            Once you agree, payments can&apos;t be refunded.
          </p>
          <p>
            Pressing <strong>Agree &amp; continue</strong> means you accept our policy of
            non-refundable payments for all purchases including subscriptions and token packs.
          </p>
          {itemLabel && (
            <p className="mt-3 pt-3 border-t border-rose-200 font-mono text-[12px] text-rose-700">
              You&apos;re purchasing: <strong>{itemLabel}</strong>
            </p>
          )}
        </div>

        <label
          className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-ink-50 border border-ink-200 hover:bg-ink-100 transition-colors"
          data-testid="non-refundable-agree-row"
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-ink-300 accent-rose-600 cursor-pointer"
            data-testid="non-refundable-agree-checkbox"
          />
          <span className="text-sm text-ink-950">
            I understand and accept that <strong>this payment is final and non-refundable</strong>.
          </span>
        </label>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="non-refundable-cancel-btn"
            className="h-11 border-ink-200 text-ink-950 text-xs font-bold uppercase tracking-[0.18em]"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={!agreed}
            data-testid="non-refundable-confirm-btn"
            className="h-11 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-[0.18em] gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShieldAlert className="w-4 h-4" />
            Agree &amp; continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NonRefundableConfirmModal;
